/**
 * The deferred "move the stage AND send the invite" action.
 *
 * The pair waits out ONE countdown together, so stopping it leaves Lever
 * untouched as well — a recruiter who confirms once should be able to undo
 * once. Split out of useBoardActions to keep that file under the line limit.
 */
import { showToast } from '@finn-auto/design-system/atoms/toast';
import * as api from '../../services/taActionBoardApi';
import type { FeedbackItem, RoleBoard } from '../../types/taActionBoard';
import type { OutboundEmail } from './outboundEmail';
import type { PendingAction } from './usePendingActions';
import { bryqOutcomeText } from '../../services/bryqApi';

export function moveEmailAction({
  item,
  role,
  stageName,
  email,
  gmailToken,
  move,
  unmarkHandled,
}: {
  item: FeedbackItem;
  role: RoleBoard;
  stageName: string;
  email: OutboundEmail;
  gmailToken: string | null;
  move: () => Promise<unknown>;
  unmarkHandled: (opportunityId: string) => void;
}): Omit<PendingAction, 'secondsLeft'> {
  return {
    id: `move:${item.opportunityId}`,
    // See PendingAction.affects — an optimistic marker must not outlive the tab.
    affects: [item.opportunityId],
    label: `Moving ${item.name} to ${stageName} and sending the email${email.bryq ? ' + Bryq' : ''}`,
    run: () => {
      let moved = false;
      move()
        .then(() => {
          moved = true;
          return api.sendCandidateEmail(
            {
              opportunityId: item.opportunityId,
              subject: email.subject,
              body: email.body,
              kind: 'move-invite',
              roleTitle: role.roleTitle,
              stageName,
              attachment: email.attachment,
              bryq: email.bryq ?? null,
            },
            gmailToken
          );
        })
        .then((r) =>
          showToast({
            status: 'success',
            description: `${item.name} moved to ${stageName} — email sent to ${r.to}${
              r.attachmentName ? ` with ${r.attachmentName}` : ''
            }.${bryqOutcomeText(r.bryq)}`,
          })
        )
        .catch((e) => {
          // Say which half failed: reporting "move done" for a failed move sends
          // the recruiter looking for a stage change that never happened.
          const why = e instanceof Error && e.message ? e.message : 'unknown error';
          if (!moved) unmarkHandled(item.opportunityId);
          showToast({
            status: 'error',
            description: moved
              ? `${item.name} was moved, but the email was NOT sent: ${why}`
              : `Nothing happened — the move failed: ${why}`,
          });
        });
    },
    undo: () => {
      unmarkHandled(item.opportunityId);
      showToast({
        status: 'info',
        description: `Stopped — ${item.name} was not moved and no email was sent.`,
      });
    },
  };
}
